/**
 * The in-call verbs that were routes with no button: transfer, add people,
 * merge, and move to a meeting.
 *
 * Each one ends the call you are on, changes who is on it, or moves you to
 * another room — so each one navigates as well as mutating, and a mutation that
 * succeeded server-side while leaving you staring at a dead room would be worse
 * than the missing button it replaces.
 */
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import { ROUTES } from '@/constants/routes';
import { callsApi, type TransferMode } from '../data/callsApi';
import { CALL_KEYS } from '../data/queryKeys';
import { pendingTransferActions } from '../store/pendingTransferStore';
import { useCallSurfaceExit } from './useCallSurfaceExit';
import { useOpenCall } from './useOpenCall';

export interface TransferRequest {
  mode: TransferMode;
  targetUserId: string;
  targetName: string;
}

export interface CallActions {
  transfer(request: TransferRequest): void;
  addPeople(userIds: readonly string[]): void;
  merge(otherCallId: string): void;
  escalate(): void;
  readonly isBusy: boolean;
}

export function useCallActions(callId: string): CallActions {
  const exit = useCallSurfaceExit();
  const openCall = useOpenCall();
  const queryClient = useQueryClient();
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
  }, [queryClient]);

  const transfer = useMutation({
    mutationFn: (request: TransferRequest) =>
      callsApi
        .transfer(callId, { mode: request.mode, targetUserId: request.targetUserId })
        .then((started) => ({ started, request })),
    onSuccess: ({ started, request }) => {
      refresh();
      if (started.mode === 'blind' || started.consultCallId === null) {
        /* A blind transfer takes YOU off the call the instant the target starts
           ringing, so staying on its page would show a room you have left. In a
           desktop call window there IS no page to go to — `/calls` there would
           turn the call window into a second workspace — so `exit.leave`
           closes it instead. */
        toast.success(`Transferring to ${request.targetName}.`);
        exit.leave(ROUTES.calls);
        return;
      }
      pendingTransferActions.start({
        transferId: started.transferId,
        originalCallId: callId,
        consultCallId: started.consultCallId,
        targetName: request.targetName,
      });
      /* The consult leg is a CALL, so it goes wherever calls go — decided
         once, in `useOpenCall`. Inside a desktop call window that is this
         window, which is already a call surface; in a browser it is this
         document. `startsConnected: false` because the target has not picked
         up yet. */
      openCall({
        callId: started.consultCallId,
        peerName: request.targetName,
        kind: 'audio',
        startsConnected: false,
      });
    },
    onError: () => toast.error('Could not transfer that call.'),
  });

  const addPeople = useMutation({
    mutationFn: (userIds: readonly string[]) => callsApi.addParticipants(callId, userIds),
    onSuccess: (result) => {
      refresh();
      /* THE SERVER ALREADY SAID SO. `added` is zero when the ring planner
         refused everyone — the commonest case by far being that the person is
         on another call and their busy-on-busy setting says no. A green
         "Ringing them now." over a phone that never rang is the worst kind of
         wrong: the user waits, then blames the callee. */
      if (result.added === 0) {
        toast.error('Could not ring them. They may already be on another call.');
        return;
      }
      toast.success('Ringing them now.');
    },
    onError: () => toast.error('Could not add them to this call.'),
  });

  const merge = useMutation({
    mutationFn: (otherCallId: string) => callsApi.merge(callId, otherCallId),
    onSuccess: () => {
      refresh();
      toast.success('Calls merged.');
    },
    onError: () => toast.error('Could not merge those calls.'),
  });

  const escalate = useMutation({
    mutationFn: () => callsApi.escalate(callId),
    onSuccess: (result) => {
      refresh();
      /* A MEETING IS NOT A CALL, so it does not belong in a window the shell
         keeps keyed to a call. Navigating here turned the call window into a
         full meeting room attached to a call that had just ended: its close
         button hung up nothing and it never closed itself. The workspace goes
         to the meeting and this window gets out of the way. */
      exit.moveTo(ROUTES.meeting(result.meetingId));
    },
    onError: () => toast.error('Could not move this call to a meeting.'),
  });

  return {
    transfer: useCallback((request: TransferRequest) => transfer.mutate(request), [transfer]),
    addPeople: useCallback((userIds: readonly string[]) => addPeople.mutate(userIds), [addPeople]),
    merge: useCallback((otherCallId: string) => merge.mutate(otherCallId), [merge]),
    escalate: useCallback(() => escalate.mutate(), [escalate]),
    isBusy:
      transfer.isPending || addPeople.isPending || merge.isPending || escalate.isPending,
  };
}
